import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

type AlertChannel = 'email' | 'discord';

interface AlertConfig {
  enabled: boolean;
  channel: AlertChannel;
  recipients: string[];
  discordWebhookUrl: string;
  discordMaskCpf: boolean;
  renotifyMinutes: number;
  emailRetryMinutes: number;
  /** Sem campo na tela: viaja no payload porque o PATCH sobrescreve o JSONB inteiro. */
  maxCasesPerMessage: number;
  minBalance: number | null;
  ignoreBefore: string | null;
}

const ALERT_CONFIG_KEY = 'negative_balance_alert';
const SYSTEM_CONFIG_RESOURCE = 'admin/system-config';

const EMPTY_ALERT_CONFIG: AlertConfig = {
  enabled: false,
  channel: 'email',
  recipients: [],
  discordWebhookUrl: '',
  discordMaskCpf: true,
  renotifyMinutes: 180,
  emailRetryMinutes: 30,
  maxCasesPerMessage: 200,
  minBalance: null,
  ignoreBefore: null,
};

const SLATE = '#64748b';

/** Validação só para o feedback na tela; o Joi do backend é o gate de verdade. */
const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** `datetime-local` não aceita ISO com timezone; converte nos dois sentidos. */
const isoToLocalInput = (iso: string | null): string => {
  if (!iso) { return ''; }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) { return ''; }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const localInputToIso = (value: string): string | null => {
  if (!value) { return null; }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const RecipientList: React.FC<{
  recipients: string[];
  onChange: (recipients: string[]) => void;
}> = ({ recipients, onChange }) => (
  <Box>
    {recipients.map((email, i) => {
      const invalid = !!email && !isValidEmail(email);
      return (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
          <TextField
            size="small"
            label="E-mail"
            placeholder="alguem@pontuax.com.br"
            value={email}
            onChange={(e) => onChange(recipients.map((x, j) => (j === i ? e.target.value : x)))}
            error={invalid}
            helperText={invalid ? 'E-mail inválido' : undefined}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => onChange(recipients.filter((_, j) => j !== i))}
            aria-label="Remover destinatário"
            sx={{ mt: 0.5 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    })}
    {!recipients.length && (
      <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 1 }}>
        Nenhum destinatário — nenhum alerta será enviado.
      </Typography>
    )}
    <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...recipients, ''])} sx={{ textTransform: 'none' }}>
      Adicionar destinatário
    </Button>
  </Box>
);

const NegativeBalanceAlertConfigDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [config, setConfig] = useState<AlertConfig>(EMPTY_ALERT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    dataProvider.getOne(SYSTEM_CONFIG_RESOURCE, { id: ALERT_CONFIG_KEY })
      .then((res) => {
        const { id, ...rest } = (res.data ?? {}) as any;
        setConfig({ ...EMPTY_ALERT_CONFIG, ...rest });
      })
      .catch(() => notify('Não foi possível carregar a configuração de alertas', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [open, dataProvider, notify]);

  const patch = (fields: Partial<AlertConfig>) => setConfig((c) => ({ ...c, ...fields }));

  /**
   * Enquanto o alerta fica desligado as ocorrências acumulam pendentes — sem o corte, o
   * primeiro envio despejaria o histórico inteiro. Fica editável antes de salvar.
   */
  const toggleEnabled = (enabled: boolean) => patch({
    enabled,
    ignoreBefore: enabled && !config.ignoreBefore ? new Date().toISOString() : config.ignoreBefore,
  });

  const buildPayload = (): AlertConfig => ({
    enabled: config.enabled,
    channel: config.channel,
    recipients: config.recipients.map((e) => e.trim()).filter(isValidEmail),
    discordWebhookUrl: config.discordWebhookUrl.trim(),
    discordMaskCpf: config.discordMaskCpf,
    renotifyMinutes: Number(config.renotifyMinutes) || EMPTY_ALERT_CONFIG.renotifyMinutes,
    emailRetryMinutes: Number(config.emailRetryMinutes) || EMPTY_ALERT_CONFIG.emailRetryMinutes,
    maxCasesPerMessage: Number(config.maxCasesPerMessage) || EMPTY_ALERT_CONFIG.maxCasesPerMessage,
    minBalance: config.minBalance === null || config.minBalance === undefined || Number.isNaN(Number(config.minBalance))
      ? null
      : Number(config.minBalance),
    ignoreBefore: config.ignoreBefore,
  });

  const handleSave = async () => {
    const payload = buildPayload();

    if (payload.enabled && payload.channel === 'discord' && !payload.discordWebhookUrl) {
      notify('Informe a URL do webhook do Discord para ativar os envios.', { type: 'warning' });
      return;
    }
    if (payload.enabled && payload.channel === 'email' && !payload.recipients.length) {
      notify('Adicione ao menos um destinatário para ativar os envios.', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // previousData vazio de propósito: o dataProvider faz dirty-diff por chave e
      // enviaria só os campos alterados — o backend valida o payload inteiro.
      await dataProvider.update(SYSTEM_CONFIG_RESOURCE, {
        id: ALERT_CONFIG_KEY,
        data: payload,
        previousData: {} as any,
      });
      notify('Configuração de alertas salva', { type: 'success' });
      onClose();
    } catch (e: any) {
      notify(e?.message || 'Erro ao salvar a configuração', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const isDiscord = config.channel === 'discord';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alertas de saldo negativo</DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <FormControlLabel
          control={<Switch checked={config.enabled} onChange={(e) => toggleEnabled(e.target.checked)} />}
          label="Enviar alertas"
        />
        <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 2 }}>
          Desligado, as ocorrências continuam sendo registradas e aparecem neste relatório — só não sai mensagem.
        </Typography>

        <TextField
          select
          fullWidth
          size="small"
          label="Canal"
          value={config.channel}
          onChange={(e) => patch({ channel: e.target.value as AlertChannel })}
          helperText="Por onde o alerta sai. Um canal por vez."
          sx={{ mb: 2 }}
        >
          <MenuItem value="email">E-mail</MenuItem>
          <MenuItem value="discord">Discord</MenuItem>
        </TextField>

        {isDiscord ? (
          <>
            <TextField
              fullWidth
              size="small"
              label="Webhook do Discord"
              value={config.discordWebhookUrl}
              onChange={(e) => patch({ discordWebhookUrl: e.target.value })}
              placeholder="https://discord.com/api/webhooks/..."
              helperText="A URL já é o destino e o segredo — não precisa de lista de destinatários."
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.discordMaskCpf}
                  onChange={(e) => patch({ discordMaskCpf: e.target.checked })}
                />
              }
              label="Mascarar o CPF na mensagem"
            />
            <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 2 }}>
              Um canal do Discord tem audiência mais larga e histórico pesquisável que a lista de e-mail.
              Quem precisa do CPF inteiro abre este relatório.
            </Typography>
          </>
        ) : (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Destinatários</Typography>
            <RecipientList
              recipients={config.recipients}
              onChange={(recipients) => patch({ recipients })}
            />
            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Reaviso (min)"
            value={config.renotifyMinutes}
            onChange={(e) => patch({ renotifyMinutes: Number(e.target.value) })}
            helperText="Silêncio por cliente após avisar"
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            label="Retentativa (min)"
            value={config.emailRetryMinutes}
            onChange={(e) => patch({ emailRetryMinutes: Number(e.target.value) })}
            helperText="Espera após falha no envio"
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Filtros do aviso</Typography>
        {/*
          Nota, não campo: o que está descrito aqui é regra fixa no backend
          (SILENCED_TRIGGER_TYPES e MIN_OCCURRENCE_AGE_MINUTES do
          NegativeBalanceAlertWorker). Fica na tela porque é a resposta para "por que
          parou de chegar aviso de cancelamento", e é aqui que se vem procurar.
        */}
        <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 2 }}>
          Cancelamento de venda não gera aviso: o estorno derruba o saldo porque o cliente já tinha
          gastado os pontos, e isso não é falha. Também não avisa o caso que se resolve em até 1
          hora. As duas situações continuam gravadas, e o cancelamento esperado sai da lista por
          default — o filtro "Tipo de caso" mostra. Se o cliente já estava negativo antes do
          cancelamento, o aviso sai, porque aí o buraco é maior do que o estorno explica.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Saldo máximo"
            value={config.minBalance ?? ''}
            onChange={(e) => patch({ minBalance: e.target.value === '' ? null : Number(e.target.value) })}
            helperText="Só alerta saldo até este valor. Vazio = qualquer negativo."
            inputProps={{ max: 0 }}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="Ignorar antes de"
            value={isoToLocalInput(config.ignoreBefore)}
            onChange={(e) => patch({ ignoreBefore: localInputToIso(e.target.value) })}
            InputLabelProps={{ shrink: true }}
            helperText="Ocorrências anteriores não geram aviso"
            sx={{ flex: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading} sx={{ textTransform: 'none' }}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NegativeBalanceAlertConfigDialog;
